<template>
  <div class="manager-commission-dashboard">
    <!-- Header with global statistics -->
    <div class="dashboard-header">
      <h2>Commission Referral Overview</h2>

      <div class="global-stats-cards">
        <div class="stat-card total">
          <h3>Total Paid</h3>
          <p class="stat-value">{{ formatCurrency(globalStats.total_commissions_paid) }}</p>
          <span class="stat-label">All commissions</span>
        </div>

        <div class="stat-card monthly">
          <h3>This Month</h3>
          <p class="stat-value">{{ formatCurrency(globalStats.monthly_commissions) }}</p>
          <span class="stat-label">{{ getCurrentMonth() }}</span>
        </div>

        <div class="stat-card pending">
          <h3>Pending</h3>
          <p class="stat-value pending-amount">{{ formatCurrency(globalStats.pending_commissions) }}</p>
          <span class="stat-label">To validate</span>
        </div>

        <div class="stat-card referrers">
          <h3>Active Referrers</h3>
          <p class="stat-value">{{ globalStats.total_referrers }}</p>
          <span class="stat-label">Users</span>
        </div>

        <div class="stat-card transactions">
          <h3>Total Transactions</h3>
          <p class="stat-value">{{ globalStats.total_transactions }}</p>
          <span class="stat-label">All commissions</span>
        </div>
      </div>
    </div>

    <!-- Quick actions -->
    <div class="quick-actions">
      <button @click="refreshData" :disabled="loadingState" class="action-btn refresh">
        {{ loadingState ? 'Loading...' : 'Refresh' }}
      </button>

      <button @click="exportData" class="action-btn export">
        Export Data
      </button>

      <button @click="showFilters = !showFilters" class="action-btn filter">
        {{ showFilters ? 'Hide' : 'Show' }} Filters
      </button>
    </div>

    <!-- Advanced filters (optional) -->
    <div v-if="showFilters" class="advanced-filters">
      <div class="filter-row">
        <div class="filter-group">
          <label>Analysis Period:</label>
          <select v-model="analysePeriod" @change="refreshData">
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="quarter">Last 3 months</option>
            <option value="year">Last 12 months</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Minimum Amount:</label>
          <input
            type="number"
            v-model="minAmount"
            placeholder="Ex: 1000"
            @change="refreshData"
          >
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="errorMessage" class="error-state">
      <p>{{ errorMessage }}</p>
      <button @click="retryLoading" class="retry-btn">Retry</button>
    </div>

    <!-- Main content -->
    <div v-if="!loadingState && !errorMessage" class="dashboard-content">

      <!-- Top Referrers -->
      <div class="top-referrers-section">
        <div class="section-header">
          <h3>Top Referrers</h3>
          <span class="section-subtitle">Best commission generators</span>
        </div>

        <div class="top-referrers-grid">
          <div
            v-for="(referrer, index) in topReferrers"
            :key="referrer.referrer__user__id"
            class="referrer-card"
            :class="{ 'top-performer': index < 3 }"
          >
            <div class="referrer-rank">{{ index + 1 }}</div>
            <div class="referrer-info">
              <h4>{{ referrer.referrer__user__username }}</h4>
              <p class="referrer-stats">
                {{ referrer.transaction_count }} transactions
              </p>
            </div>
            <div class="referrer-earnings">
              {{ formatCurrency(referrer.total_earned) }}
            </div>
            <div class="referrer-actions">
              <button @click="viewReferrerDetail(referrer)" class="view-detail-btn">
                View Details
              </button>
            </div>
          </div>
        </div>

        <div v-if="topReferrers.length === 0" class="no-data">
          <p>No active referrers at the moment</p>
        </div>
      </div>

      <!-- Recent Commissions -->
      <div class="recent-commissions-section">
        <div class="section-header">
          <h3>Recent Commissions</h3>
          <span class="section-subtitle">Last 20 transactions</span>
        </div>

        <div class="commissions-table-container">
          <table class="recent-commissions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Referrer</th>
                <th>Referred</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="commission in recentCommissions" :key="commission.id">
                <td>{{ formatDateTime(commission.created_at) }}</td>
                <td class="referrer-name">{{ commission.referrer_username }}</td>
                <td class="referred-name">{{ commission.referred_user_username }}</td>
                <td>
                  <span :class="['commission-type-badge', getTypeClass(commission.commission_type)]">
                    {{ getTypeLabel(commission.commission_type) }}
                  </span>
                </td>
                <td class="commission-amount">{{ formatCurrency(commission.commission_amount) }}</td>
                <td>
                  <span :class="['status-badge', getStatusClass(commission.status)]">
                    {{ getStatusLabel(commission.status) }}
                  </span>
                </td>
                <td>
                  <button
                    @click="viewCommissionDetail(commission)"
                    class="action-btn view-btn small"
                  >
                    View
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="recentCommissions.length === 0" class="no-data">
          <p>No recent commissions</p>
        </div>
      </div>

      <!-- Charts and Analytics -->
      <div class="analytics-section">
        <div class="section-header">
          <h3>Trends</h3>
          <span class="section-subtitle">Commission evolution</span>
        </div>

        <div class="analytics-content">
          <!-- Performance Metrics -->
          <div class="performance-metrics">
            <div class="metric-card">
              <div class="metric-icon">📈</div>
              <div class="metric-info">
                <h4>Growth Rate</h4>
                <p class="metric-value positive">{{ calculateGrowthRate() }}%</p>
                <span class="metric-period">vs last month</span>
              </div>
            </div>

            <div class="metric-card">
              <div class="metric-icon">💰</div>
              <div class="metric-info">
                <h4>Avg Commission</h4>
                <p class="metric-value">{{ formatCurrency(calculateAverageCommission()) }}</p>
                <span class="metric-period">per transaction</span>
              </div>
            </div>

            <div class="metric-card">
              <div class="metric-icon">👥</div>
              <div class="metric-info">
                <h4>Top Performer</h4>
                <p class="metric-value">{{ getTopPerformer() }}</p>
                <span class="metric-period">highest earner</span>
              </div>
            </div>

            <div class="metric-card">
              <div class="metric-icon">🎯</div>
              <div class="metric-info">
                <h4>Success Rate</h4>
                <p class="metric-value positive">{{ calculateSuccessRate() }}%</p>
                <span class="metric-period">paid commissions</span>
              </div>
            </div>
          </div>

          <!-- Simple Trend Visualization -->
          <div class="trend-visualization">
            <div class="trend-header">
              <h4>Commission Distribution</h4>
              <p>By commission type</p>
            </div>

            <div class="trend-bars">
              <div class="trend-bar-item" v-for="trend in commissionTrends" :key="trend.type">
                <div class="trend-bar-label">{{ trend.type }}</div>
                <div class="trend-bar-container">
                  <div
                    class="trend-bar"
                    :style="{ width: trend.percentage + '%', backgroundColor: trend.color }"
                  ></div>
                </div>
                <div class="trend-bar-value">{{ trend.count }} ({{ trend.percentage }}%)</div>
              </div>
            </div>
          </div>

          <!-- Status Overview -->
          <div class="status-overview">
            <div class="status-header">
              <h4>Commission Status</h4>
              <p>Current distribution</p>
            </div>

            <div class="status-grid">
              <div class="status-item paid">
                <div class="status-count">{{ getStatusCount('PAID') }}</div>
                <div class="status-label">Paid</div>
              </div>

              <div class="status-item pending">
                <div class="status-count">{{ getStatusCount('PENDING') }}</div>
                <div class="status-label">Pending</div>
              </div>

              <div class="status-item cancelled">
                <div class="status-count">{{ getStatusCount('CANCELLED') }}</div>
                <div class="status-label">Cancelled</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Commission detail modal -->
    <div v-if="selectedCommission" class="modal-overlay" @click="closeModal">
      <div class="modal-content commission-detail-modal" @click.stop>
        <div class="modal-header">
          <h3>Commission Details #{{ selectedCommission.id }}</h3>
          <button @click="closeModal" class="close-btn">&times;</button>
        </div>

        <div class="modal-body">
          <div class="detail-section">
            <h4>General Information</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Creation Date:</span>
                <span class="detail-value">{{ formatDateTime(selectedCommission.created_at) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Type:</span>
                <span class="detail-value">{{ getTypeLabel(selectedCommission.commission_type) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Status:</span>
                <span :class="['detail-value', 'status-badge', getStatusClass(selectedCommission.status)]">
                  {{ getStatusLabel(selectedCommission.status) }}
                </span>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Users</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Referrer:</span>
                <span class="detail-value">{{ selectedCommission.referrer_username }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Referred User:</span>
                <span class="detail-value">{{ selectedCommission.referred_user_username }}</span>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Amounts</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Commission:</span>
                <span class="detail-value commission-highlight">
                  {{ formatCurrency(selectedCommission.commission_amount) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Referrer detail modal -->
    <div v-if="selectedReferrer" class="modal-overlay" @click="closeReferrerModal">
      <div class="modal-content referrer-detail-modal" @click.stop>
        <div class="modal-header">
          <h3>Referrer Details - {{ selectedReferrer.referrer__user__username }}</h3>
          <button @click="closeReferrerModal" class="close-btn">&times;</button>
        </div>

        <div class="modal-body">
          <div class="referrer-summary">
            <div class="summary-stat">
              <span class="summary-label">Total Earned:</span>
              <span class="summary-value">{{ formatCurrency(selectedReferrer.total_earned) }}</span>
            </div>
            <div class="summary-stat">
              <span class="summary-label">Number of Transactions:</span>
              <span class="summary-value">{{ selectedReferrer.transaction_count }}</span>
            </div>
          </div>

          <div class="referrer-actions-section">
            <p>Available actions for this referrer</p>
            <div class="action-buttons">
              <button class="btn primary">View All Commissions</button>
              <button class="btn secondary">Contact Referrer</button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeReferrerModal" class="btn secondary">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ManagerCommissionDashboard',

  data() {
    return {
      globalStats: {
        total_commissions_paid: 0,
        monthly_commissions: 0,
        pending_commissions: 0,
        total_referrers: 0,
        total_transactions: 0
      },
      topReferrers: [],
      recentCommissions: [],

      // États de l'interface
      loadingState: false,
      errorMessage: null,
      retryAttempts: 0,
      showFilters: false,

      // Filtres
      analysePeriod: 'month',
      minAmount: '',

      // Modals
      selectedCommission: null,
      selectedReferrer: null
    };
  },

  computed: {
    commissionTrends() {
      const trends = {};
      let total = this.recentCommissions.length;

      this.recentCommissions.forEach(commission => {
        const type = this.getTypeLabel(commission.commission_type);
        trends[type] = (trends[type] || 0) + 1;
      });

      const colors = {
        'Withdrawal Fee': '#4facfe',
        'Deposit Fee': '#667eea'
      };

      return Object.entries(trends).map(([type, count]) => ({
        type,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
        color: colors[type] || '#9ca3af'
      }));
    }
  },

  async created() {
    await this.initializeComponent();
  },

  methods: {
    async initializeComponent() {
      try {
        await this.loadManagerData();
      } catch (error) {
        this.handleInitializationError(error);
      }
    },

    async loadManagerData() {
      if (this.retryAttempts > 2) return;

      this.loadingState = true;
      this.errorMessage = null;

      try {
        const response = await axios.get('/api/v1/manager/commissions/overview/', {
          params: {
            period: this.analysePeriod,
            min_amount: this.minAmount || undefined
          },
          timeout: 15000
        });

        const data = response.data;
        this.globalStats = data.global_stats || {};
        this.topReferrers = data.top_referrers || [];
        this.recentCommissions = data.recent_commissions || [];
        this.retryAttempts = 0;

      } catch (error) {
        this.handleDataError(error);
      } finally {
        this.loadingState = false;
      }
    },

    async refreshData() {
      this.retryAttempts = 0;
      await this.loadManagerData();
    },

    exportData() {
      // Implémentation de l'export (CSV, Excel, etc.)
      console.log('Export des données de commission');
      // Placeholder - à implémenter selon vos besoins
    },

    viewCommissionDetail(commission) {
      this.selectedCommission = commission;
    },

    viewReferrerDetail(referrer) {
      this.selectedReferrer = referrer;
    },

    closeModal() {
      this.selectedCommission = null;
    },

    closeReferrerModal() {
      this.selectedReferrer = null;
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0 FCFA';
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount).replace('XAF', 'FCFA');
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getCurrentMonth() {
      return new Date().toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    },

    getTypeLabel(type) {
      const labels = {
        'WITHDRAWAL_FEE': 'Withdrawal Fee',
        'DEPOSIT_FEE': 'Deposit Fee'
      };
      return labels[type] || type;
    },

    getTypeClass(type) {
      return type.toLowerCase().replace('_', '-');
    },

    getStatusLabel(status) {
      const labels = {
        'PAID': 'Paid',
        'PENDING': 'Pending',
        'CANCELLED': 'Cancelled'
      };
      return labels[status] || status;
    },

    getStatusClass(status) {
      return `status-${status.toLowerCase()}`;
    },

    calculateGrowthRate() {
      // Simplified growth rate calculation based on recent data
      const recentCount = this.recentCommissions.filter(c => {
        const date = new Date(c.created_at);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return date >= thirtyDaysAgo;
      }).length;

      const olderCount = this.recentCommissions.length - recentCount;

      if (olderCount === 0) return recentCount > 0 ? 100 : 0;
      return Math.round(((recentCount - olderCount) / olderCount) * 100);
    },

    calculateAverageCommission() {
      if (this.recentCommissions.length === 0) return 0;
      const total = this.recentCommissions.reduce((sum, c) => sum + c.commission_amount, 0);
      return total / this.recentCommissions.length;
    },

    getTopPerformer() {
      return this.topReferrers.length > 0 ? this.topReferrers[0].referrer__user__username : 'N/A';
    },

    calculateSuccessRate() {
      if (this.recentCommissions.length === 0) return 0;
      const paidCount = this.recentCommissions.filter(c => c.status === 'PAID').length;
      return Math.round((paidCount / this.recentCommissions.length) * 100);
    },

    getStatusCount(status) {
      return this.recentCommissions.filter(c => c.status === status).length;
    },

    handleDataError(error) {
      this.errorMessage = error.response?.data?.error ||
                        error.message ||
                        'Error loading data';
      this.retryAttempts++;

      if (this.retryAttempts <= 2) {
        setTimeout(() => this.loadManagerData(), 2000 * this.retryAttempts);
      }
    },

    handleInitializationError(error) {
      this.errorMessage = `Initialization error: ${error.message}`;
      console.error('Dashboard manager initialization error:', error);
    },

    retryLoading() {
      this.retryAttempts = 0;
      this.initializeComponent();
    }
  }
};
</script>

<style scoped>
/* Dashboard Manager Commissions - CSS Styles */

.manager-commission-dashboard {
  padding: 32px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-sizing: border-box;
  overflow-x: hidden;
}

.manager-commission-dashboard * {
  box-sizing: border-box;
}

/* Main header */
.dashboard-header h2 {
  color: #1a202c;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 24px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Global statistics */
.global-stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
  width: 100%;
}

.stat-card {
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
  width: 100%;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.stat-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.stat-card.total::before {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.stat-card.monthly::before {
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card.pending::before {
  background: linear-gradient(90deg, #f093fb 0%, #f5576c 100%);
}

.stat-card.referrers::before {
  background: linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%);
}

.stat-card.transactions::before {
  background: linear-gradient(90deg, #ffecd2 0%, #fcb69f 100%);
}

.stat-card h3 {
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-card .stat-value {
  color: #1a202c;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 4px 0;
  text-align: right;
}

.stat-card .pending-amount {
  color: #e53e3e;
}

.stat-card .stat-label {
  color: #718096;
  font-size: 12px;
  font-weight: 500;
}

/* Quick actions */
.quick-actions {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  width: 100%;
}

.action-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn.refresh {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.action-btn.refresh:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
}

.action-btn.export {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(78, 205, 196, 0.4);
}

.action-btn.export:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(78, 205, 196, 0.5);
}

.action-btn.filter {
  background: white;
  color: #4a5568;
  border: 2px solid #e2e8f0;
}

.action-btn.filter:hover {
  border-color: #667eea;
  color: #667eea;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Advanced filters */
.advanced-filters {
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  width: 100%;
}

.filter-row {
  display: flex;
  gap: 24px;
  align-items: flex-end;
  flex-wrap: wrap;
  width: 100%;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
  flex: 1;
}

.filter-group label {
  color: #4a5568;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.filter-group select,
.filter-group input {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: all 0.2s ease;
  width: 100%;
}

.filter-group select:focus,
.filter-group input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Error and loading states */
.error-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 16px;
  margin: 24px 0;
  color: #e53e3e;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
}

.retry-btn {
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  margin-top: 16px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(229, 62, 62, 0.4);
}

/* Main sections */
.dashboard-content {
  display: grid;
  gap: 32px;
  width: 100%;
}

.section-header {
  margin-bottom: 20px;
}

.section-header h3 {
  color: #1a202c;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.section-subtitle {
  color: #718096;
  font-size: 14px;
  font-weight: 500;
}

/* Top referrers */
.top-referrers-section {
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.8);
  width: 100%;
}

.top-referrers-grid {
  display: grid;
  gap: 16px;
  width: 100%;
}

.referrer-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f7fafc;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  width: 100%;
}

.referrer-card:hover {
  border-color: #667eea;
  background: #edf2f7;
  transform: translateX(4px);
}

.referrer-card.top-performer {
  background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
  border-color: #f56565;
}

.referrer-card.top-performer .referrer-rank {
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  color: white;
}

.referrer-rank {
  background: #667eea;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
}

.referrer-info {
  flex: 1;
  min-width: 0;
}

.referrer-info h4 {
  color: #1a202c;
  margin: 0 0 4px 0;
  font-weight: 600;
  font-size: 16px;
}

.referrer-stats {
  color: #718096;
  margin: 0;
  font-size: 13px;
}

.referrer-earnings {
  color: #38a169;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
}

.referrer-actions {
  flex-shrink: 0;
}

.view-detail-btn {
  background: #667eea;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-detail-btn:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

/* Recent commissions */
.recent-commissions-section {
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  width: 100%;
  overflow: hidden;
}

.commissions-table-container {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  width: 100%;
}

.recent-commissions-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.recent-commissions-table thead {
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
}

.recent-commissions-table th {
  padding: 16px 20px;
  text-align: left;
  font-weight: 700;
  color: #4a5568;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 2px solid #e2e8f0;
}

.recent-commissions-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #2d3748;
}

.recent-commissions-table tbody tr {
  transition: all 0.2s ease;
}

.recent-commissions-table tbody tr:hover {
  background: #f8fafc;
}

.referrer-name, .referred-name {
  font-weight: 600;
  color: #4a5568;
}

.commission-type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.commission-type-badge.withdrawal-fee {
  background: linear-gradient(135deg, #fef5e7 0%, #fed7aa 100%);
  color: #9c4221;
}

.commission-type-badge.deposit-fee {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
}

.commission-amount {
  font-weight: 700;
  color: #38a169;
  font-size: 15px;
}

.status-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.status-paid {
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  color: #22543d;
}

.status-badge.status-pending {
  background: linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%);
  color: #9c4221;
}

.status-badge.status-cancelled {
  background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
  color: #742a2a;
}

.action-btn.view-btn.small {
  padding: 6px 12px;
  font-size: 11px;
  border-radius: 6px;
}

/* Analytics section */
.analytics-section {
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  width: 100%;
}

.analytics-content {
  display: grid;
  gap: 24px;
  width: 100%;
}

.performance-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  width: 100%;
}

.metric-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.metric-card:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.metric-info {
  flex: 1;
  min-width: 0;
}

.metric-info h4 {
  color: #4a5568;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 4px 0;
}

.metric-value {
  color: #1a202c;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 2px 0;
}

.metric-value.positive {
  color: #38a169;
}

.metric-period {
  color: #718096;
  font-size: 11px;
}

.trend-visualization {
  background: #f7fafc;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
}

.trend-header {
  margin-bottom: 20px;
}

.trend-header h4 {
  color: #1a202c;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.trend-header p {
  color: #718096;
  font-size: 12px;
  margin: 0;
}

.trend-bars {
  display: grid;
  gap: 12px;
  width: 100%;
}

.trend-bar-item {
  display: grid;
  grid-template-columns: 120px 1fr auto;
  align-items: center;
  gap: 12px;
}

.trend-bar-label {
  color: #4a5568;
  font-weight: 600;
  font-size: 12px;
}

.trend-bar-container {
  background: #e2e8f0;
  border-radius: 6px;
  height: 8px;
  overflow: hidden;
}

.trend-bar {
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s ease;
}

.trend-bar-value {
  color: #718096;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.status-overview {
  background: #f7fafc;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
}

.status-header {
  margin-bottom: 20px;
}

.status-header h4 {
  color: #1a202c;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.status-header p {
  color: #718096;
  font-size: 12px;
  margin: 0;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 16px;
  width: 100%;
}

.status-item {
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-item.paid {
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  border: 2px solid #68d391;
}

.status-item.pending {
  background: linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%);
  border: 2px solid #f6ad55;
}

.status-item.cancelled {
  background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
  border: 2px solid #fc8181;
}

.status-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.status-count {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.status-label {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* No data messages */
.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #718096;
  background: #f7fafc;
  border-radius: 12px;
  border: 2px dashed #e2e8f0;
  width: 100%;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.modal-content {
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 90%;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-weight: 700;
  font-size: 20px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body {
  padding: 32px;
  overflow-y: auto;
}

/* Commission detail modal */
.commission-detail-modal .detail-section {
  margin-bottom: 24px;
}

.commission-detail-modal .detail-section h4 {
  color: #1a202c;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

.detail-grid {
  display: grid;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.detail-label {
  color: #718096;
  font-weight: 500;
  font-size: 14px;
}

.detail-value {
  color: #1a202c;
  font-weight: 600;
  font-size: 14px;
  text-align: right;
}

.commission-highlight {
  color: #38a169 !important;
  font-size: 18px;
  font-weight: 700;
}

/* Referrer detail modal */
.referrer-detail-modal .referrer-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: #f7fafc;
  border-radius: 12px;
}

.summary-stat {
  text-align: center;
}

.summary-label {
  display: block;
  color: #718096;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.summary-value {
  color: #1a202c;
  font-size: 20px;
  font-weight: 700;
}

.referrer-actions-section {
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.referrer-actions-section p {
  color: #4a5568;
  margin-bottom: 16px;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.modal-footer {
  padding: 20px 32px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-size: 14px;
}

.btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn.secondary {
  background: white;
  color: #4a5568;
  border: 2px solid #e2e8f0;
}

.btn.secondary:hover {
  border-color: #cbd5e0;
  background: #f7fafc;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .global-stats-cards {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

@media (max-width: 768px) {
  .manager-commission-dashboard {
    padding: 16px;
  }

  .dashboard-header h2 {
    font-size: 24px;
  }

  .global-stats-cards {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .stat-card {
    padding: 20px;
  }

  .stat-card .stat-value {
    font-size: 24px;
  }

  .quick-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .filter-row {
    flex-direction: column;
  }

  .filter-group {
    min-width: auto;
    width: 100%;
  }

  .top-referrers-section,
  .recent-commissions-section,
  .analytics-section {
    padding: 20px;
  }

  .section-header h3 {
    font-size: 20px;
  }

  .referrer-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .referrer-info {
    text-align: center;
  }

  .performance-metrics {
    grid-template-columns: 1fr;
  }

  .commissions-table-container {
    overflow-x: auto;
  }

  .recent-commissions-table {
    min-width: 700px;
  }

  .modal-content {
    margin: 20px;
    width: calc(100% - 40px);
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 16px 20px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .recent-commissions-table th,
  .recent-commissions-table td {
    padding: 12px 8px;
    font-size: 12px;
  }

  .referrer-card {
    padding: 16px;
  }

  .commission-amount {
    font-size: 14px;
  }

  .stat-card .stat-value {
    font-size: 20px;
  }

  .trend-bar-item {
    grid-template-columns: 80px 1fr auto;
    gap: 8px;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }
}

/* Entry animations */
.top-referrers-section,
.recent-commissions-section,
.analytics-section {
  animation: slideInUp 0.6s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInScale 0.6s ease-out;
  animation-fill-mode: both;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }
.stat-card:nth-child(5) { animation-delay: 0.5s; }

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

/* Focus effects for accessibility */
.action-btn:focus,
.btn:focus,
.view-detail-btn:focus {
  outline: 3px solid rgba(102, 126, 234, 0.3);
  outline-offset: 2px;
}

.filter-group select:focus,
.filter-group input:focus {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Advanced loading states */
.loading-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

/* Tooltips (optional) */
.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1a202c;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1001;
}

.tooltip:hover::after {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(-4px);
}
</style>
